import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import { Loader2, Save, X } from 'lucide-react'

interface DayAvailability {
  day_of_week: number
  day_name: string
  is_available: boolean
  is_all_day: boolean
  start_time: string | null
  end_time: string | null
  slot_interval_minutes: number | null
  buffer_minutes?: number | null
}

interface StaffAvailabilityPanelProps {
  userId: string
  memberName: string
  onClose: () => void
}

export function StaffAvailabilityPanel({ userId, memberName, onClose }: StaffAvailabilityPanelProps) {
  const { t } = useTranslation('dashboard')
  const queryClient = useQueryClient()
  const [localDays, setLocalDays] = useState<DayAvailability[]>([])

  const { data, isLoading } = useQuery<{ days: DayAvailability[] }>({
    queryKey: ['staff-availability', userId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: { days: DayAvailability[] } }>(
        `/user/staff/${userId}/availability`
      )
      return response.data.data
    },
  })

  useEffect(() => {
    if (data?.days) {
      setLocalDays(data.days)
    }
  }, [data])

  const updateMutation = useMutation({
    mutationFn: async (days: DayAvailability[]) => {
      await apiClient.put(`/user/staff/${userId}/availability`, { days })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability', userId] })
    },
  })

  const handleToggleDay = (dayOfWeek: number) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek
          ? {
              ...day,
              is_available: !day.is_available,
              start_time: day.is_available ? null : '09:00',
              end_time: day.is_available ? null : '17:00',
            }
          : day
      )
    )
  }

  const handleToggleAllDay = (dayOfWeek: number) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek ? { ...day, is_all_day: !day.is_all_day } : day
      )
    )
  }

  const handleTimeChange = (dayOfWeek: number, field: 'start_time' | 'end_time', value: string) => {
    setLocalDays((prev) =>
      prev.map((day) => (day.day_of_week === dayOfWeek ? { ...day, [field]: value } : day))
    )
  }

  const handleIntervalChange = (dayOfWeek: number, value: number | null) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek ? { ...day, slot_interval_minutes: value } : day
      )
    )
  }

  const hasCustomSchedule = localDays.some((d) => d.is_available)
  const hasChanges = JSON.stringify(data?.days) !== JSON.stringify(localDays)

  return (
    <div className="rounded-lg border border-border p-6 space-y-4 bg-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-medium">
            {t('team.schedule.title', { defaultValue: 'Schedule at this location' })}
          </h3>
          <p className="text-sm text-muted-foreground">{memberName}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('team.schedule.hint', {
              defaultValue:
                'Leave all days off to use the location default hours. Set hours here when this person works different times.',
            })}
          </p>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {localDays.map((day) => (
              <div
                key={day.day_of_week}
                className={`p-3 rounded-lg border ${
                  day.is_available ? 'border-primary/40 bg-primary/5' : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={day.is_available}
                      onChange={() => handleToggleDay(day.day_of_week)}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    {day.day_name}
                  </label>
                  {day.is_available && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={day.is_all_day}
                        onChange={() => handleToggleAllDay(day.day_of_week)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      {t('availability.schedule.allDay', { defaultValue: 'All day' })}
                    </label>
                  )}
                </div>
                {day.is_available && !day.is_all_day && (
                  <div className="grid grid-cols-3 gap-2 ml-6">
                    <input
                      type="time"
                      value={day.start_time || ''}
                      onChange={(e) => handleTimeChange(day.day_of_week, 'start_time', e.target.value)}
                      className="px-2 py-1 text-sm border rounded-md bg-background"
                    />
                    <input
                      type="time"
                      value={day.end_time || ''}
                      onChange={(e) => handleTimeChange(day.day_of_week, 'end_time', e.target.value)}
                      className="px-2 py-1 text-sm border rounded-md bg-background"
                    />
                    <select
                      value={day.slot_interval_minutes || ''}
                      onChange={(e) =>
                        handleIntervalChange(
                          day.day_of_week,
                          e.target.value ? parseInt(e.target.value, 10) : null
                        )
                      }
                      className="px-2 py-1 text-sm border rounded-md bg-background"
                    >
                      <option value="">Default interval</option>
                      <option value="15">15 min</option>
                      <option value="30">30 min</option>
                      <option value="60">60 min</option>
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!hasCustomSchedule && (
            <p className="text-sm text-muted-foreground">
              {t('team.schedule.usingLocationHours', {
                defaultValue: 'Using location default hours for all days.',
              })}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('services.form.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="button"
              onClick={() => updateMutation.mutate(localDays)}
              disabled={!hasChanges || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('availability.saveChanges', { defaultValue: 'Save changes' })}
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
